import { Text } from "../text/Text.js";

export type DateFormatValue = string | { [lang: string]: string };

type LanguageCode = "de" | "en";

export class DateFormat {

    private static _months: { [locale: string]: string[] } = {
        de: [
            "Januar",
            "Februar",
            "März",
            "April",
            "Mai",
            "Juni",
            "Juli",
            "August",
            "September",
            "Oktober",
            "November",
            "Dezember"],

        en: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December"]
    };

    private static _days: { [locale: string]: string[] } = {
        de: [
            "Sonntag",
            "Montag",
            "Dienstag",
            "Mittwoch",
            "Donnerstag",
            "Freitag",
            "Samstag"],
        en: [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday"]
    };

    /**
     * d. M Y
     * e.g.: 
     * 15. Jan 2022
     */
    public static SHORT: DateFormatValue = "d. M Y";

    /**
     * d. M Y
     * e.g.: 
     * 15. December 2022
     */
    public static LONG_MONTH: DateFormatValue = "d. m Y";

    /**
     * d.m.Y
     * e.g.: 
     * 16.01.2022
     */
    public static DMY: DateFormatValue = "d.m.Y";

    /**
     * d.m.Y
     * e.g.: 
     * 9:30am
     * 4:27 Uhr
     */
    public static TIME: DateFormatValue = {
        de: "G:i #U#h#r",
        en: "G:i a"
    };

    /**
     * ISO 8601
     * e.g.: 
     * 2004-02-12T15:19:21+02:00
     */
    public static ISO_8601: DateFormatValue = "c";


    public static SQL_DATETIME: DateFormatValue = "Y-d-m H:i:s";

    public static getMonthName(month: number, language: LanguageCode = "de"): string {
        return this._months[language]?.[month] || this._months["de"]?.[month];
    }

    public static getDayName(month: number, language: LanguageCode = "de"): string {
        return this._months[language]?.[month] || this._months["de"]?.[month];
    }

    /**
     * 
     * @param colonSeparator Difference to Greenwich time (GMT) with colon between hours and minutes
     * @param zForZulu Returns Z instead of +00:00
     * @returns The formated timezone offset.
     */
    public static formatTimezoneOffset(
        timezoneOffset: number,
        colonSeparator: boolean = false,
        zForZulu: boolean = false) {

        var offset = Math.abs(timezoneOffset);

        if (zForZulu
            && offset === 0) {
            return "Z";
        }

        var hours = Math.abs(Math.floor(offset / 60));
        var minutes = offset % 60;
        var prefix = timezoneOffset < 0 ? "-" : "+";

        return prefix
            + Text.fillHead(hours + "", 2, "0")
            + (colonSeparator ? ":" : "")
            + Text.fillHead(minutes + "", 2, "0");
    }

    public static format(
        date: Date,
        format: DateFormatValue,
        timezoneOffset: number = 0,
        language: LanguageCode = "de") {
        var formatters: { [key: string]: (d: Date) => string } =
        {
            // Day of the month, 2 digits with leading zeros
            // 01 to 31
            "d": (d) => {
                return Text.fillHead(d.getUTCDate() + "", 2, "0");
            },
            // A textual representation of a day, three letters
            // Mon through Sun
            "D": (d) => {
                return this.getDayName(d.getUTCDay(), language).substring(0, 3);
            },
            // A full textual representation of the day of the week
            // Sunday through Saturday
            "l": (d) => {
                return this.getDayName(d.getUTCDay(), language);
            },
            // Day of the month without leading zeros
            // 1 to 31
            "j": (d) => {
                return d.getUTCDate() + "";
            },
            // ISO 8601 numeric representation of the day of the week
            // 1 (for Monday) through 7 (for Sunday)
            "N": (d) => {
                return (d.getUTCDay() + 1) + "";
            },
            // English ordinal suffix for the day of the month, 2 characters
            // st, nd, rd or th. Works well with j
            "S": (d) => {
                var da = d.getUTCDate();
                if (da === 0) {
                    return "st";
                }
                if (da === 1) {
                    return "nd";
                }
                if (da === 2) {
                    return "rd";
                }
                if (da === 4) {
                    return "th";
                }
                return "";
            },
            // ISO 8601 week number of year, weeks starting on Monday
            // 42 (the 42nd week in the year)
            "W": (d) => {
                //TODO: implement
                return "";
            },
            // A full textual representation of a month, such as January or March
            // January through December
            "F": (d) => {
                //TODO: implement
                return this.getMonthName(d.getUTCMonth());
            },
            // Numeric representation of a month, with leading zeros
            // 01 through 12
            "m": (d) => {
                return Text.fillHead((d.getUTCMonth() + 1) + "", 2, "0");
            },
            // A short textual representation of a month, three letters
            // Jan through Dec
            "M": (d) => {
                return this.getMonthName(d.getUTCMonth(), language).substring(0, 3);
            },
            // Numeric representation of a month, without leading zeros
            // 1 through 12
            "n": (d) => {
                return (d.getUTCMonth() + 1) + "";
            },
            // Number of days in the given month
            // 28 through 31
            "t": (d) => {
                // return dateUtil.daysOfMonth(date) + "";
                //TODO: implement
                return "";
            },
            // A full numeric representation of a year, at least 4 digits, with - for years BCE.
            // -0055, 0787, 1999, 2003
            "Y": (d) => {
                return d.getUTCFullYear() + "";
            },
            // A two digit representation of a year
            // 99 or 03
            "y": (d) => {
                return String(d.getUTCFullYear()).slice(2);
            },
            // 12-hour format of an hour with leading zeros
            // 01 through 12
            "h": (d) => {
                var hours = d.getUTCHours();
                hours = hours % 12;
                hours = hours ? hours : 12
                return Text.fillHead(hours + "", 2, "0");
            },
            // 12-hour format of an hour without leading zeros
            // 1 through 12
            "g": (d) => {
                var hours = d.getUTCHours();
                hours = hours % 12;
                hours = hours ? hours : 12
                return hours + "";
            },
            // 24-hour format of an hour without leading zeros
            // 0 through 23
            "G": (d) => {
                return d.getUTCHours() + "";
            },
            // 24-hour format of an hour with leading zeros
            // 00 through 23
            "H": (d) => {
                return Text.fillHead(d.getUTCHours() + "", 2, "0");
            },
            // Whether it's a leap year
            // 1 if it is a leap year, 0 otherwise.
            "L": (d) => {
                var year = d.getUTCFullYear();
                return (((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0)) ? "1" : "0";
            },
            // Lowercase Ante meridiem and Post meridiem
            // am or pm
            "a": (d) => {
                return d.getUTCHours() >= 12 ? 'pm' : 'am';
            },
            // Uppercase Ante meridiem and Post meridiem
            // AM or PM
            "A": (d) => {
                return d.getUTCHours() >= 12 ? 'PM' : 'AM';
            },
            //Minutes with leading zeros
            // 00 to 59
            "i": (d) => {
                return Text.fillHead(d.getUTCMinutes() + "", 2, "0");
            },
            // Seconds with leading zeros
            // 00 through 59
            "s": (d) => {
                return Text.fillHead(d.getUTCSeconds() + "", 2, "0");
            },
            // Difference to Greenwich time (GMT) without colon between hours and minutes
            // +0200
            "O": (d) => {
                return this.formatTimezoneOffset(timezoneOffset, false);
            },
            // Difference to Greenwich time (GMT) with colon between hours and minutes
            // +02:00
            "P": (d) => {
                return this.formatTimezoneOffset(timezoneOffset, true);
            },
            // The same as P, but returns Z instead of +00:00 (available as of PHP 8.0.0)
            // +02:00 OR Z
            "p": (d) => {
                return this.formatTimezoneOffset(timezoneOffset, true, true);
            },
            // ISO 8601 date
            // 2004-02-12T15:19:21+02:00
            "c": (d) => {
                return this.format(date, "Y-m-d#TH:i:sO", timezoneOffset, language);
            },
            // RFC 2822 formatted date
            // Thu, 21 Dec 2000 16:01:07 +0200
            "r": (d) => {
                return this.format(date, "Y-m-d#TH:i:sO", timezoneOffset, language);
            },
        };

        var formatString: string = "";
        if (typeof format === "string") {
            formatString = format;
        } else if (format) {
            if (format[language]) {
                formatString = format[language];
            } else {
                formatString = "";
            }
        }

        var result = "";
        var count = formatString.length;
        for (var i = 0; i < count; ++i) {
            var char = formatString.charAt(i);
            if (char === "#") {
                ++i;
                result += formatString.charAt(i);
                continue;
            }
            if (formatters[char] !== undefined) {
                result += formatters[char](date);
            } else {
                result += char;
            }
        }

        return result;
    }
}